/**
 * Per-applicant credit-report PDF preview (combined Verification Summary).
 * The PDF is a private Azure blob, so the <iframe> points at the app's
 * download-credit-report-blob route with `disposition=inline` (the route
 * proxies + auth/tenant-scopes the blob). Lazy by construction: Radix unmounts
 * the dialog body when closed, so the iframe — and the multi-MB PDF fetch —
 * only mounts on open and never touches initial page load.
 *
 * The PDF takes a moment to fetch + render, so a loading overlay covers the
 * blank iframe until its `load` event fires. The iframe lives in its OWN child
 * component that mounts fresh each open, so the loading state resets every time
 * without a useEffect.
 */
import { Download, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { demoCreditPreviewSrc } from '#app/components/application-detail-v2/demo/demo-hooks'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '#app/components/ui/dialog'

function CreditIframe({ url }: { url: string }) {
	const [loading, setLoading] = useState(true)
	return (
		<div className="relative min-h-0 flex-1">
			{loading ? (
				<div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white">
					<Loader2 className="size-9 animate-spin text-[#32302F]" />
					<span className="text-sm font-medium text-gray-500">
						Loading credit report…
					</span>
				</div>
			) : null}
			<iframe
				title="Credit report preview"
				src={url}
				onLoad={() => setLoading(false)}
				className="h-full w-full border-0"
			/>
		</div>
	)
}

export function CreditPreviewDialog({
	open,
	onOpenChange,
	applicationId,
	propertyId,
	applicantName,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	applicationId: string | null
	propertyId: string
	applicantName: string | null
}) {
	const baseUrl =
		demoCreditPreviewSrc(propertyId) ??
		(applicationId
			? `/resources/download-credit-report-blob?${new URLSearchParams({
					applicationId,
					propertyId,
				})}`
			: null)

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="flex h-[90vh] w-[95vw] max-w-6xl flex-col gap-0 overflow-hidden rounded-3xl border-[#ECECEA] bg-white p-0 sm:max-w-6xl">
				<DialogHeader className="flex flex-row items-center justify-between border-b border-[#F3F3F3] px-6 py-4 pr-12">
					<DialogTitle className="text-base font-semibold text-[#32302F]">
						Credit Report{applicantName ? ` — ${applicantName}` : ''}
					</DialogTitle>
					{baseUrl ? (
						<a
							href={baseUrl}
							className="flex shrink-0 items-center gap-1.5 rounded-full border border-[#ECECEA] bg-white px-3.5 py-1.5 text-xs font-medium text-[#32302F] transition-colors hover:bg-[#F6F7F5]"
						>
							<Download className="h-3.5 w-3.5 text-gray-500" />
							Download
						</a>
					) : null}
				</DialogHeader>
				{open && baseUrl ? (
					<CreditIframe url={`${baseUrl}&disposition=inline`} />
				) : null}
			</DialogContent>
		</Dialog>
	)
}
